import { Box, Dialog, Divider, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { LATEST } from "../../../utils/constants";
import PracticeItem from "./components/PracticeItem";
import SubPillarSection from "./components/SubPillarSection";
import HeatmapPracticeDrawer from "./HeatmapPracticeDrawer";

const HeatmapDialogPractice = ({
  open,
  onClose,
  cell,
  showRisk,
  selectedTimePoint,
}) => {
  const colors = useColors();
  const [openDrawerPractice, setOpenDrawerPractice] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const { trackEvent } = useAPI();

  useEffect(() => {
    if (selectedPractice && selectedTimePoint === LATEST) {
      setOpenDrawerPractice(true);
      trackEvent(
        "User opened drawer practice " +
          selectedPractice.name +
          " in Sub Pillar " +
          selectedPractice.subPillar
      );
    }
  }, [selectedPractice]);

  const extractCellData = useMemo(() => {
    if (!cell)
      return {
        severity: "N/A",
        entityType: "",
        category: "",
        cellColor: colors.grey_300,
        practices: [],
      };

    const severity = cell?.data?.y?.value ?? "N/A";
    const practices = cell?.data?.y?.practice;
    const entityType = cell?.data?.x ?? "";
    const category = cell?.serieId ?? cell?.data?.id ?? "";
    const cellColor = cell?.color ?? colors.grey_300;

    return { severity, entityType, category, cellColor, practices };
  }, [cell]);

  const { severity, entityType, category, cellColor, practices } =
    extractCellData;
  const [selectedSubPillar, setSelectedSubPillar] = useState(null);

  // Set the first sub pillar as selected by default when practices change
  useEffect(() => {
    if (practices && practices.length > 0 && !selectedSubPillar) {
      setSelectedSubPillar(practices[0]);
    }
  }, [practices, cell]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setSelectedPractice(null);
        setSelectedSubPillar(null);
        setOpenDrawerPractice(false);
        trackEvent("User closed a practice dialog in the heatmap");
      }}
      sx={{
        zIndex: 100,
      }}
      PaperProps={{
        style: {
          backgroundColor: colors.grey_50,
          width: "620px",
          maxWidth: "100%",
          overflowY: "visible",
        },
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          borderRadius: 1,
          boxShadow: "none",
          padding: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* --------Header-------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                bgcolor: cellColor,
                width: "4px",
                height: "auto",
                borderRadius: 2,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: colors.text_secondary }}
              >
                Security Domain
              </Typography>
              <Typography variant="subtitle2">{category}</Typography>
            </Box>
          </Box>
          <Divider flexItem orientation="vertical" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              Infrastructure Pillar
            </Typography>
            <Typography variant="subtitle2">{entityType}</Typography>
          </Box>
        </Box>
        <Divider />
        {/* -------- Render the Sub Pillar tabs -------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "50%",
            }}
          >
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              Sub Pillar
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {practices?.map((item, itemIndex) => (
                <SubPillarSection
                  key={`type-${itemIndex}`}
                  entityType={entityType}
                  typeData={item}
                  showRisk={showRisk}
                  setSelectedSubPillar={setSelectedSubPillar}
                  selectedSubPillar={selectedSubPillar}
                />
              ))}
            </Box>
          </Box>
          {/* -------- Render the Security Practices section -------- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "50%",
            }}
          >
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              Security Practices
            </Typography>
            <Box
              sx={{
                width: "300px",
                display: "flex",
                height: "100%",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 4,
                flexWrap: "wrap",
                borderRadius: "8px",
                border: "1px solid " + colors.divider,
                background: colors.common_white,
                padding: "10px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {selectedSubPillar?.practices?.map((practice, index) => (
                  <PracticeItem
                    key={`practice-${index}`}
                    practice={practice}
                    setSelectedPractice={setSelectedPractice}
                    showRisk={showRisk}
                    selectedTimePoint={selectedTimePoint}
                  />
                ))}
                {(!selectedSubPillar?.practices ||
                  selectedSubPillar.practices.length === 0) && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text_secondary, padding: "10px 5px" }}
                  >
                    No security practices available for this subpillar
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <HeatmapPracticeDrawer
          isOpen={openDrawerPractice}
          onClose={() => {
            trackEvent("User closed a practice dialog in the heatmap");
            setSelectedPractice(null);
            setOpenDrawerPractice(false);
          }}
          practice={selectedPractice}
          entityType={entityType}
          domain={category}
          showRisk={showRisk}
        />
      </Box>
    </Dialog>
  );
};

export default HeatmapDialogPractice;
